import React, { useEffect, useRef, useState } from "react";

/**
 * "What are capital projects?" — editorial redesign (Feb 2026).
 *
 * Design moves:
 *   • Asymmetric triptych — three story panels, each anchored by a giant
 *     count-up numeral; numerals alternate left/right for natural reading flow.
 *   • Glass-morphic stat block over a faint orthographic grid background
 *     (pure SVG, theme-aware via currentColor) for depth.
 *   • Ember rim-light on hover, subtle z-index layering between numeral and text.
 *   • Industries rendered as glass pills with hover micro-interactions, not a
 *     middot list.
 *   • Reuses CSS tokens (--steel, --ember, --text-*, --bg-*) so dark/light parity
 *     is automatic. All scoped styles colocated in this file (no App.css bloat).
 */

const PANELS = [
  {
    id: "scale",
    end: 12.3,
    decimals: 1,
    prefix: "$",
    suffix: "T",
    caption: "Global construction market · 2025",
    tag: "01 / Scale",
    body: (
      <>
        Every refinery that processes fuel in Texas or Gujarat, every
        offshore wind farm in the North Sea, every metro line in Riyadh or
        Mumbai, every LNG terminal in Qatar or Mozambique &mdash; these are
        capital projects. Multi-year, multi-billion-dollar programs that
        transform raw materials into the infrastructure of modern
        civilization.
      </>
    ),
  },
  {
    id: "ambition",
    end: 106,
    decimals: 0,
    prefix: "$",
    suffix: "T",
    caption: "Cumulative infra investment · McKinsey, through 2040",
    tag: "02 / Ambition",
    body: (
      <>
        The construction market is projected to reach{" "}
        <strong>$16 trillion</strong> by 2030, and McKinsey estimates{" "}
        <strong>$106 trillion</strong> in cumulative infrastructure
        investment is needed through 2040 &mdash; spanning energy,
        transport, water, digital, and social infrastructure across every
        continent.
      </>
    ),
  },
  {
    id: "complexity",
    end: 1.5,
    decimals: 1,
    prefix: "",
    suffix: "M",
    caption: "Engineering docs · single mid-sized refinery",
    tag: "03 / Complexity",
    body: (
      <>
        A mid-sized refinery generates <strong>1.5M</strong> engineering
        documents across 15 disciplines. A 1 GW renewable park coordinates
        200+ vendors across 4 countries. A petrochemical complex takes 6
        years from FEED to mechanical completion. And every one of these
        projects runs over budget, over schedule, or both.
      </>
    ),
  },
];

/* Industries — bespoke 32×32 SVG glyph + sub-categories + descriptive note.
   Glyphs in 1.4px stroke, theme-aware via currentColor. Pure SVG, no icon library. */
const INDUSTRIES = [
  {
    name: "Oil, Gas & Energy",
    sub: "Upstream · Midstream · LNG · Downstream · Hydrogen · CCUS",
    note: "From the Permian Basin to Jamnagar, from North Sea platforms to East African LNG.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 28 L12 6 L20 6 L26 28" />
        <path d="M9 17 L23 17" />
        <path d="M11 22 L21 22" />
        <path d="M16 6 L16 2" />
        <circle cx="16" cy="2" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: "Power & Renewables",
    sub: "Thermal · Nuclear · Solar · Wind · Hydro · BESS · T&D",
    note: "IEA projects $3 trillion annually in energy investment by 2030. Every watt needs engineering.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 17 L16 28" />
        <circle cx="16" cy="16" r="1.6" fill="currentColor" />
        <path d="M16 14.4 L9 6" />
        <path d="M17.5 16.7 L28 13" />
        <path d="M14.7 17 L9 26" />
      </svg>
    ),
  },
  {
    name: "Chemicals & Petrochemicals",
    sub: "Basic · Specialty · Fertilizers · Polymers",
    note: "$200B+ in new petrochemical capacity under construction globally — US Gulf Coast, Middle East, India.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 4 L12 13 L6 26 L26 26 L20 13 L20 4" />
        <path d="M11 4 L21 4" />
        <path d="M9.5 19 L22.5 19" />
        <circle cx="14" cy="22" r="0.9" fill="currentColor" />
        <circle cx="18" cy="23" r="0.9" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: "Infrastructure & Transport",
    sub: "Highways · Metros · Railways · Airports · Bridges · Tunnels",
    note: "$97 trillion in global infrastructure demand through 2040.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 22 L29 22" />
        <path d="M3 22 L8 10 L24 10 L29 22" />
        <path d="M11 10 L11 4" />
        <path d="M21 10 L21 4" />
        <path d="M16 10 L16 22" />
        <path d="M3 26 L29 26" strokeDasharray="2 3" />
      </svg>
    ),
  },
  {
    name: "Metals, Mining & Minerals",
    sub: "Steel · Aluminium · Copper · Lithium · Rare Earth · Cement",
    note: "The energy transition runs on metals. Every gigawatt of renewables needs 3× the mineral input of fossil.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 26 L16 8 L28 26 Z" />
        <path d="M11 18 L21 18" />
        <path d="M16 8 L16 4" />
        <path d="M13 22 L19 22" />
      </svg>
    ),
  },
  {
    name: "Manufacturing & Life Sciences",
    sub: "Life Sciences · Pulp & Paper · Semiconductors · Discrete",
    note: "From pharmaceutical GMP facilities to chip fabs — precision-engineered, compliance-heavy capital projects.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M13 4 L13 13 L7 26 L25 26 L19 13 L19 4" />
        <path d="M11 4 L21 4" />
        <circle cx="13" cy="20" r="1.1" fill="currentColor" />
        <circle cx="18" cy="22" r="1.1" fill="currentColor" />
        <circle cx="15" cy="23.5" r="0.8" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: "Marine & Offshore",
    sub: "Ports · Shipbuilding · FPSO · Offshore Wind Foundations",
    note: "From Rotterdam to Singapore — maritime infrastructure at industrial scale.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 22 L27 22 L24 28 L8 28 Z" />
        <path d="M16 22 L16 6" />
        <path d="M16 6 L25 14" />
        <path d="M16 6 L7 14" />
        <path d="M3 28 Q8 26 12 28 T20 28 T28 28" />
      </svg>
    ),
  },
  {
    name: "Data Centers & Digital",
    sub: "Hyperscale · Colocation · Edge · Subsea Cable",
    note: "Global data center capex exceeding $690B in 2026 — the fastest-growing capital projects vertical on Earth.",
    glyph: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="5" y="5" width="22" height="6" rx="1" />
        <rect x="5" y="13" width="22" height="6" rx="1" />
        <rect x="5" y="21" width="22" height="6" rx="1" />
        <circle cx="9" cy="8" r="0.9" fill="currentColor" />
        <circle cx="9" cy="16" r="0.9" fill="currentColor" />
        <circle cx="9" cy="24" r="0.9" fill="currentColor" />
        <path d="M14 8 L23 8" />
        <path d="M14 16 L23 16" />
        <path d="M14 24 L23 24" />
      </svg>
    ),
  },
];

/* Inline count-up — eased cubic, fires once on viewport entry. */
function StatNumeral({ end, decimals = 0, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVal(end);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const dur = 1600;
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.45 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end]);
  const display = decimals
    ? val.toFixed(decimals)
    : Math.round(val).toLocaleString("en-IN");
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export default function WhatAreCapitalProjects() {
  return (
    <section
      className="cx-edu reveal"
      data-testid="home-edu"
      aria-labelledby="home-edu-h2"
    >
      {/* Background — orthographic grid + ember radial wash */}
      <div className="cx-edu__bg" aria-hidden="true">
        <svg className="cx-edu__grid" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="cxEduGrid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.18" />
            </pattern>
            <radialGradient id="cxEduFade" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="#000" stopOpacity="1" />
              <stop offset="100%" stopColor="#000" stopOpacity="0" />
            </radialGradient>
            <mask id="cxEduMask">
              <rect width="100%" height="100%" fill="url(#cxEduFade)" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="url(#cxEduGrid)" mask="url(#cxEduMask)" />
        </svg>
        <div className="cx-edu__ember" />
      </div>

      <div className="container cx-edu__inner">
        {/* Editorial header */}
        <header className="cx-edu__head">
          <div className="section-label">
            <span className="dot" />
            The built world
          </div>
          <h2 className="display cx-edu__title" id="home-edu-h2" data-testid="home-edu-h2">
            Capital projects are how the physical world{" "}
            <em>gets engineered, procured, and constructed.</em>
          </h2>
          <div className="cx-edu__rule" aria-hidden="true">
            <span className="cx-edu__rule-dot" />
          </div>
        </header>

        {/* Triptych — alternating asymmetric panels */}
        <div className="cx-edu__panels" data-testid="home-edu-panels">
          {PANELS.map((p, i) => (
            <article
              key={p.id}
              className={`cx-edu__panel cx-edu__panel--${i % 2 === 0 ? "left" : "right"} reveal`}
              style={{ "--d": `${i * 140}ms` }}
              data-testid={`home-edu-panel-${p.id}`}
            >
              <div className="cx-edu__numeral" aria-hidden="true">
                <span className="cx-edu__numeral-tag mono">{p.tag}</span>
                <span className="cx-edu__numeral-value">
                  <StatNumeral
                    end={p.end}
                    decimals={p.decimals}
                    prefix={p.prefix}
                    suffix={p.suffix}
                  />
                </span>
                <span className="cx-edu__numeral-cap mono">{p.caption}</span>
              </div>
              <div className="cx-edu__copy">
                <p>{p.body}</p>
              </div>
            </article>
          ))}
        </div>

        {/* Industries — merged: editorial sector grid + sub-categories + descriptive note */}
        <div className="cx-edu__industries" data-testid="home-industries" aria-labelledby="home-industries-h2">
          <div className="cx-edu__industries-head">
            <div>
              <span className="mono cx-edu__industries-eyebrow">
                Industries served · Eight sectors
              </span>
              <h3 className="cx-edu__industries-title display" id="home-industries-h2" data-testid="home-industries-h2">
                Built for the industries that{" "}
                <em>build everything else.</em>
              </h3>
            </div>
            <span className="cx-edu__industries-count mono">
              {String(INDUSTRIES.length).padStart(2, "0")} / 08
            </span>
          </div>
          <ul className="cx-edu__sector-grid">
            {INDUSTRIES.map((ind, i) => (
              <li
                key={ind.name}
                className="cx-edu__sector reveal"
                style={{ "--d": `${i * 60}ms` }}
                data-testid={`home-industry-${i}`}
              >
                <div className="cx-edu__sector-top">
                  <span className="cx-edu__sector-num mono">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="cx-edu__sector-glyph" aria-hidden="true">
                    {ind.glyph}
                  </span>
                </div>
                <div className="cx-edu__sector-name">{ind.name}</div>
                <div className="cx-edu__sector-sub">{ind.sub}</div>
                <div className="cx-edu__sector-note">{ind.note}</div>
                <span className="cx-edu__sector-rule" aria-hidden="true" />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Scoped styles — colocated to avoid App.css bloat */}
      <style>{`
        .cx-edu {
          position: relative;
          padding: 96px 0 88px;
          overflow: hidden;
          isolation: isolate;
          background: var(--bg);
        }
        .cx-edu__bg {
          position: absolute;
          inset: 0;
          z-index: 0;
          color: var(--text-3);
          pointer-events: none;
        }
        .cx-edu__grid {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0.55;
        }
        .cx-edu__ember {
          position: absolute;
          left: -10%;
          top: 30%;
          width: 60vw;
          height: 60vw;
          max-width: 900px;
          max-height: 900px;
          background: radial-gradient(
            circle at center,
            rgba(242, 107, 31, 0.10) 0%,
            rgba(242, 107, 31, 0.04) 30%,
            transparent 60%
          );
          filter: blur(40px);
          border-radius: 50%;
        }
        [data-theme="dark"] .cx-edu__ember {
          background: radial-gradient(
            circle at center,
            rgba(248, 148, 78, 0.14) 0%,
            rgba(248, 148, 78, 0.05) 30%,
            transparent 60%
          );
        }
        .cx-edu__inner {
          position: relative;
          z-index: 1;
        }

        /* Header */
        .cx-edu__head {
          max-width: 920px;
          margin-bottom: 64px;
        }
        .cx-edu__title {
          margin-top: 14px;
          font-size: clamp(30px, 4vw, 52px);
          line-height: 1.08;
          letter-spacing: -0.02em;
          font-weight: 400;
          color: var(--text);
        }
        .cx-edu__title em {
          font-style: italic;
          color: var(--text-3);
        }
        [data-theme="dark"] .cx-edu__title em {
          color: var(--steel-200);
        }
        .cx-edu__rule {
          margin-top: 24px;
          width: 200px;
          height: 1px;
          background: linear-gradient(
            90deg,
            var(--ember) 0%,
            var(--ember) 30%,
            var(--border) 100%
          );
          position: relative;
        }
        .cx-edu__rule-dot {
          position: absolute;
          left: 0;
          top: 50%;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--ember);
          transform: translate(-3px, -50%);
          box-shadow: 0 0 16px rgba(242, 107, 31, 0.6);
        }

        /* Triptych panels */
        .cx-edu__panels {
          display: flex;
          flex-direction: column;
          gap: 56px;
          margin-bottom: 88px;
        }
        .cx-edu__panel {
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
          gap: clamp(32px, 5vw, 84px);
          align-items: center;
          position: relative;
        }
        .cx-edu__panel--right {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
        }
        .cx-edu__panel--right .cx-edu__numeral {
          order: 2;
          text-align: right;
          align-items: flex-end;
        }
        .cx-edu__panel--right .cx-edu__copy {
          order: 1;
        }

        /* Numeral block */
        .cx-edu__numeral {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: flex-start;
          position: relative;
          padding: 24px 0;
        }
        .cx-edu__numeral::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: var(--border);
        }
        .cx-edu__panel--right .cx-edu__numeral::before {
          left: auto;
          right: 0;
          width: 100%;
        }
        .cx-edu__numeral-tag {
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--ember);
          opacity: 0.95;
        }
        .cx-edu__numeral-value {
          font-family: var(--f-display);
          font-size: clamp(64px, 8.5vw, 124px);
          line-height: 0.94;
          letter-spacing: -0.045em;
          font-weight: 300;
          color: var(--text);
          font-variant-numeric: tabular-nums;
          background: linear-gradient(
            180deg,
            var(--text) 0%,
            var(--text) 55%,
            var(--text-3) 100%
          );
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          transition: transform .5s cubic-bezier(.2,.8,.2,1), filter .5s ease;
          will-change: transform;
        }
        .cx-edu__panel:hover .cx-edu__numeral-value {
          transform: translateY(-3px);
          filter: drop-shadow(0 14px 36px rgba(242, 107, 31, 0.18));
        }
        [data-theme="dark"] .cx-edu__panel:hover .cx-edu__numeral-value {
          filter: drop-shadow(0 14px 40px rgba(248, 148, 78, 0.22));
        }
        .cx-edu__numeral-cap {
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--text-3);
          max-width: 320px;
          line-height: 1.5;
        }
        .cx-edu__panel--right .cx-edu__numeral-cap {
          text-align: right;
        }

        /* Copy block */
        .cx-edu__copy p {
          margin: 0;
          color: var(--text-2);
          font-size: 16px;
          line-height: 1.6;
          max-width: 520px;
        }
        .cx-edu__panel--right .cx-edu__copy p {
          margin-left: auto;
        }
        .cx-edu__copy strong {
          color: var(--text);
          font-weight: 600;
        }

        /* Industries — sector card grid */
        .cx-edu__industries {
          margin-top: 24px;
          padding: 56px 0 0;
          border-top: 1px solid var(--border);
        }
        .cx-edu__industries-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 24px;
          margin-bottom: 40px;
          flex-wrap: wrap;
        }
        .cx-edu__industries-eyebrow {
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--ember);
          display: block;
          margin-bottom: 14px;
        }
        .cx-edu__industries-title {
          margin: 0;
          font-size: clamp(28px, 3.6vw, 44px);
          line-height: 1.06;
          letter-spacing: -0.02em;
          font-weight: 400;
          color: var(--text);
          max-width: 720px;
        }
        .cx-edu__industries-title em {
          font-style: italic;
          color: var(--text-3);
        }
        [data-theme="dark"] .cx-edu__industries-title em {
          color: var(--steel-200);
        }
        .cx-edu__industries-count {
          font-size: 11px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--text-3);
          padding-bottom: 6px;
        }
        .cx-edu__sector-grid {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          border-top: 1px solid var(--border);
          border-left: 1px solid var(--border);
        }
        .cx-edu__sector {
          position: relative;
          padding: 32px 26px 30px;
          border-right: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          background: transparent;
          transition: background-color .4s ease;
          cursor: default;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .cx-edu__sector::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          width: 2px;
          height: 0;
          background: var(--ember);
          transition: height .45s cubic-bezier(.2,.8,.2,1);
        }
        .cx-edu__sector:hover {
          background: var(--bg-sunk);
        }
        [data-theme="dark"] .cx-edu__sector:hover {
          background: rgba(255, 255, 255, 0.025);
        }
        .cx-edu__sector:hover::before {
          height: 100%;
        }
        .cx-edu__sector-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
        }
        .cx-edu__sector-num {
          font-size: 11px;
          letter-spacing: 0.18em;
          color: var(--text-3);
          transition: color .4s ease;
        }
        .cx-edu__sector:hover .cx-edu__sector-num {
          color: var(--ember);
        }
        .cx-edu__sector-glyph {
          width: 32px;
          height: 32px;
          color: var(--text-3);
          opacity: 0.85;
          transition: color .45s ease, transform .55s cubic-bezier(.2,.8,.2,1), opacity .4s ease;
          display: inline-flex;
        }
        .cx-edu__sector-glyph svg { width: 100%; height: 100%; display: block; }
        .cx-edu__sector:hover .cx-edu__sector-glyph {
          color: var(--ember);
          opacity: 1;
          transform: translateY(-2px) rotate(-3deg);
        }
        .cx-edu__sector-name {
          font-family: var(--f-display);
          font-size: 19px;
          line-height: 1.25;
          letter-spacing: -0.01em;
          color: var(--text);
          margin-bottom: 10px;
          font-weight: 500;
        }
        .cx-edu__sector-sub {
          font-size: 13px;
          line-height: 1.55;
          color: var(--text-2);
          margin-bottom: 14px;
        }
        .cx-edu__sector-note {
          font-size: 12.5px;
          line-height: 1.55;
          color: var(--text-3);
          font-style: italic;
          margin-top: auto;
        }
        .cx-edu__sector-rule {
          position: absolute;
          left: 26px;
          right: 26px;
          bottom: 0;
          height: 1px;
          background: linear-gradient(90deg, var(--ember) 0%, transparent 100%);
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform .5s cubic-bezier(.2,.8,.2,1);
        }
        .cx-edu__sector:hover .cx-edu__sector-rule { transform: scaleX(1); }

        /* Mobile */
        @media (max-width: 880px) {
          .cx-edu { padding: 96px 0 88px; }
          .cx-edu__head { margin-bottom: 64px; }
          .cx-edu__panels { gap: 56px; margin-bottom: 80px; }
          .cx-edu__panel,
          .cx-edu__panel--right {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          .cx-edu__panel--right .cx-edu__numeral {
            order: 0;
            text-align: left;
            align-items: flex-start;
          }
          .cx-edu__panel--right .cx-edu__numeral-cap { text-align: left; }
          .cx-edu__panel--right .cx-edu__copy { order: 1; }
          .cx-edu__panel--right .cx-edu__copy p { margin-left: 0; }
          .cx-edu__numeral { padding: 24px 0; }
          .cx-edu__numeral-value { font-size: clamp(72px, 18vw, 120px); }
          .cx-edu__sector-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 480px) {
          .cx-edu__sector-grid { grid-template-columns: 1fr; }
        }

        @media (prefers-reduced-motion: reduce) {
          .cx-edu__panel:hover .cx-edu__numeral-value,
          .cx-edu__sector:hover .cx-edu__sector-glyph {
            transform: none;
            filter: none;
          }
        }
      `}</style>
    </section>
  );
}
